/**
 * Order List
 * Full order list with filtering, sorting and pagination.
 * Brand-teal industrial aesthetic.
 *
 * TypeScript since ST-00227 (was index.js). Audit batch ORD:
 *   ORD-12 Create Order and Cancel only for roles that may manage orders.
 *   ORD-14 A failed cancel is shown, not swallowed.
 *   ORD-16 No fake per-order sync badge; Type offers the types orders have (Dispatch, Receipt).
 *   ORD-18 The search says what it matches; any filter or search change goes back to page 1;
 *          a load error shows an error with Retry instead of "No orders found".
 */

import { useState, useEffect, useCallback, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Filter,
  Search,
  ChevronDown,
  ChevronUp,
  Eye,
  XCircle,
  Plus,
  ArrowLeft,
  RefreshCw,
  AlertCircle,
} from 'lucide-react';
import orderService from '../../../../services/orderService';
import useOrderSubscription from '../../../../hooks/useOrderSubscription';
import { StatusBadge, PriorityBadge, TableSkeleton, EmptyState, InlineAlert } from '../shared';
import ThemedSelect from '../../../common/ThemedSelect';
import { useAuth } from '../../../../types/commonComponents';
import { formatAppDate } from '../../../../utils/dateUtils';
import {
  apiErrorMessage,
  isOrderOpen,
  ORDER_TYPE_FILTERS,
  orderPath,
  ORDERS_MANAGE,
} from '../shared/orderHelpers';
import type { OrderRow } from '../../../../types/clientPages/orderManagement';

const MONO = { fontFamily: 'IBM Plex Mono, monospace' };
const FILTER_LABEL =
  'block text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-1.5';
const FILTER_SELECT =
  'w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm bg-surface';

interface OrderFilters {
  status: string;
  priority: string;
  type: string;
  search: string;
}

type SortField = 'order_no' | 'order_size' | 'status' | 'priority' | 'created_at';

interface SortConfig {
  field: SortField;
  direction: 'asc' | 'desc';
}

const EMPTY_FILTERS: OrderFilters = { status: '', priority: '', type: '', search: '' };

/* ─── Filter Bar ──────────────────────────── */
interface FilterBarProps {
  filters: OrderFilters;
  onFilterChange: (field: keyof OrderFilters, value: string) => void;
  onSearch: () => void;
  onReset: () => void;
}

const FilterBar = ({ filters, onFilterChange, onSearch, onReset }: FilterBarProps) => (
  <div className="bg-surface rounded-xl border border-slate-200 p-4 mb-5">
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
      {/* Status */}
      <div>
        <span className={FILTER_LABEL}>Status</span>
        <ThemedSelect
          value={filters.status}
          onChange={(e) => onFilterChange('status', String(e.target.value))}
          aria-label="Status"
          className={FILTER_SELECT}
        >
          <option value="">All Statuses</option>
          <option value="Active">Active</option>
          <option value="In Progress">In Progress</option>
          <option value="Completed">Completed</option>
          <option value="Cancelled">Cancelled</option>
          <option value="On Hold">On Hold</option>
        </ThemedSelect>
      </div>

      {/* Priority */}
      <div>
        <span className={FILTER_LABEL}>Priority</span>
        <ThemedSelect
          value={filters.priority}
          onChange={(e) => onFilterChange('priority', String(e.target.value))}
          aria-label="Priority"
          className={FILTER_SELECT}
        >
          <option value="">All Priorities</option>
          <option value="Urgent">Urgent</option>
          <option value="High">High</option>
          <option value="Normal">Normal</option>
          <option value="Low">Low</option>
        </ThemedSelect>
      </div>

      {/* Type — the types Create Order makes (ORD-16) */}
      <div>
        <span className={FILTER_LABEL}>Type</span>
        <ThemedSelect
          value={filters.type}
          onChange={(e) => onFilterChange('type', String(e.target.value))}
          aria-label="Type"
          className={FILTER_SELECT}
        >
          <option value="">All Types</option>
          {ORDER_TYPE_FILTERS.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </ThemedSelect>
      </div>

      {/* Search */}
      <div className="lg:col-span-2">
        <label htmlFor="order-search" className={FILTER_LABEL}>
          Search
        </label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            id="order-search"
            type="text"
            value={filters.search}
            onChange={(e) => onFilterChange('search', e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && onSearch()}
            placeholder="Order no, notes, reference or location..."
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
          />
        </div>
      </div>
    </div>

    <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
      <button
        onClick={onReset}
        className="text-xs text-slate-500 hover:text-slate-700 font-medium transition-colors"
      >
        Reset Filters
      </button>
      <button
        onClick={onSearch}
        className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors text-xs font-semibold flex items-center gap-1.5"
      >
        <Filter className="w-3.5 h-3.5" />
        Apply Filters
      </button>
    </div>
  </div>
);

/* ─── Order Table ─────────────────────────── */
interface SortHeaderProps {
  label: string;
  field: SortField;
  sortConfig: SortConfig;
  onSort: (field: SortField) => void;
}

const SortHeader = ({ label, field, sortConfig, onSort }: SortHeaderProps) => {
  const isSorted = sortConfig.field === field;
  const Icon = isSorted && sortConfig.direction === 'asc' ? ChevronUp : ChevronDown;
  return (
    <th
      onClick={() => onSort(field)}
      className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest cursor-pointer hover:bg-slate-100/60 transition-colors select-none"
    >
      <div className="flex items-center gap-1">
        {label}
        {isSorted && <Icon className="w-3.5 h-3.5 text-primary-500" />}
      </div>
    </th>
  );
};

type QuickAction = 'view' | 'cancel';

interface OrderTableProps {
  orders: OrderRow[];
  sortConfig: SortConfig;
  canManage: boolean;
  onSort: (field: SortField) => void;
  onRowClick: (orderNo: string) => void;
  onQuickAction: (e: MouseEvent, action: QuickAction, orderNo: string) => void;
  loading: boolean;
}

const OrderTable = ({
  orders,
  sortConfig,
  canManage,
  onSort,
  onRowClick,
  onQuickAction,
  loading,
}: OrderTableProps) => {
  if (loading) return <TableSkeleton rows={8} cols={7} />;

  const sortProps = { sortConfig, onSort };
  return (
    <div className="bg-surface rounded-xl border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50/80 border-b border-slate-100">
              <SortHeader label="Order No" field="order_no" {...sortProps} />
              <SortHeader label="Size" field="order_size" {...sortProps} />
              <SortHeader label="Status" field="status" {...sortProps} />
              <SortHeader label="Priority" field="priority" {...sortProps} />
              <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                Route
              </th>
              <SortHeader label="Created" field="created_at" {...sortProps} />
              <th className="px-5 py-3 text-right text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {orders.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <EmptyState
                    icon={Filter}
                    title="No orders found"
                    description={
                      canManage
                        ? 'Try adjusting your filters or create a new order'
                        : 'Try adjusting your filters'
                    }
                  />
                </td>
              </tr>
            ) : (
              orders.map((order, i) => (
                <tr
                  key={order.order_no}
                  onClick={() => onRowClick(order.order_no)}
                  className="hover:bg-primary-50/30 cursor-pointer transition-colors group animate-slideUp"
                  style={{ animationDelay: `${i * 30}ms` }}
                >
                  <td className="px-5 py-3.5">
                    <span className="text-sm font-bold text-slate-900" style={MONO}>
                      {order.order_no}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="text-sm text-slate-700 tabular-nums" style={MONO}>
                      {order.order_size?.toLocaleString()} MT
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <StatusBadge status={order.order_status} />
                  </td>
                  <td className="px-5 py-3.5">
                    <PriorityBadge priority={order.order_priority} />
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="text-xs text-slate-600 max-w-[180px] truncate block">
                      {order.dispatch_location_name} → {order.receipt_location_name}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="text-xs text-slate-400 tabular-nums" style={MONO}>
                      {formatAppDate(order.creation_date_time)}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => onQuickAction(e, 'view', order.order_no)}
                        className="p-1.5 rounded-md text-primary-600 hover:bg-primary-50 transition-colors"
                        title="View"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {canManage && isOrderOpen(order.order_status) && (
                        <button
                          onClick={(e) => onQuickAction(e, 'cancel', order.order_no)}
                          className="p-1.5 rounded-md text-red-500 hover:bg-red-50 transition-colors"
                          title="Cancel"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ─── Pagination ──────────────────────────── */
interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}

const Pagination = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
}: PaginationProps) => {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className="bg-surface rounded-xl border border-slate-200 px-5 py-3.5 mt-5 flex items-center justify-between">
      <span className="text-xs text-slate-500">
        <span className="font-semibold text-slate-700">
          {startItem}–{endItem}
        </span>{' '}
        of <span className="font-semibold text-slate-700">{totalItems}</span>
      </span>
      <div className="flex items-center gap-1.5">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          Previous
        </button>
        {[...Array(Math.min(totalPages, 7))].map((_, i) => {
          let page: number;
          if (totalPages <= 7) {
            page = i + 1;
          } else if (currentPage <= 4) {
            page = i < 5 ? i + 1 : i === 5 ? -1 : totalPages;
          } else if (currentPage >= totalPages - 3) {
            page = i === 0 ? 1 : i === 1 ? -1 : totalPages - (6 - i);
          } else if (i === 0) page = 1;
          else if (i === 1) page = -1;
          else if (i >= 2 && i <= 4) page = currentPage + (i - 3);
          else if (i === 5) page = -1;
          else page = totalPages;

          if (page === -1) {
            return (
              <span key={`ellipsis-${i}`} className="px-1.5 text-slate-400 text-xs">
                ...
              </span>
            );
          }

          return (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              className={`w-8 h-8 rounded-lg text-xs font-semibold transition-colors ${
                page === currentPage
                  ? 'bg-primary-500 text-white'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              {page}
            </button>
          );
        })}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          Next
        </button>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   MAIN ORDER LIST COMPONENT
   ═══════════════════════════════════════════════ */
const OrderList = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canManage = hasPermission(ORDERS_MANAGE);
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [filters, setFilters] = useState<OrderFilters>(EMPTY_FILTERS);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'created_at',
    direction: 'desc',
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20,
  });

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const response = await orderService.getOrders({
        ...filters,
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
        sort_by: sortConfig.field,
        sort_order: sortConfig.direction,
      });
      setOrders(response.orders || []);
      setLoadError(null);
      setPagination((prev) => ({
        ...prev,
        totalPages: response.total_pages || 1,
        totalItems: response.total || 0,
      }));
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      setOrders([]);
      setLoadError(apiErrorMessage(error, 'The server did not answer'));
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.currentPage, pagination.itemsPerPage, sortConfig]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useOrderSubscription({
    onOrderCreated: () => fetchOrders(),
    onOrderUpdated: () => fetchOrders(),
    onStatusChanged: () => fetchOrders(),
  });

  const goToFirstPage = () => setPagination((prev) => ({ ...prev, currentPage: 1 }));

  // ORD-18: a narrower result would leave the user on an empty later page.
  const handleFilterChange = (field: keyof OrderFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    goToFirstPage();
  };
  const handleSearch = () => {
    if (pagination.currentPage === 1) fetchOrders();
    else goToFirstPage();
  };
  const handleResetFilters = () => {
    setFilters(EMPTY_FILTERS);
    goToFirstPage();
  };
  const handleSort = (field: SortField) =>
    setSortConfig((prev) => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));

  const handleRowClick = (orderNo: string) => navigate(orderPath(orderNo));

  const cancelOrder = async (orderNo: string) => {
    setActionError(null);
    try {
      await orderService.cancelOrder(orderNo, 'Cancelled from order list');
      fetchOrders();
    } catch (err) {
      setActionError(apiErrorMessage(err, `Failed to cancel order ${orderNo}`));
    }
  };

  const handleQuickAction = (e: MouseEvent, action: QuickAction, orderNo: string) => {
    e.stopPropagation();
    if (action === 'view') {
      navigate(orderPath(orderNo));
    } else if (action === 'cancel' && window.confirm(`Cancel order ${orderNo}?`)) {
      cancelOrder(orderNo);
    }
  };

  const handlePageChange = (page: number) =>
    setPagination((prev) => ({ ...prev, currentPage: page }));

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/order-management')}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-500"
            aria-label="Back to Order Management"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1
              className="text-2xl font-bold text-slate-900"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              Orders
            </h1>
            <p className="text-sm text-slate-500">Manage and track all orders</p>
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          <button
            onClick={fetchOrders}
            className="p-2.5 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors text-slate-500"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          {canManage && (
            <button
              onClick={() => navigate('/order-management/orders/create')}
              className="px-4 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center text-sm font-semibold shadow-sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Order
            </button>
          )}
        </div>
      </div>

      <FilterBar
        filters={filters}
        onFilterChange={handleFilterChange}
        onSearch={handleSearch}
        onReset={handleResetFilters}
      />

      {actionError && (
        <InlineAlert className="mb-4" onDismiss={() => setActionError(null)}>
          {actionError}
        </InlineAlert>
      )}

      {loadError && !loading ? (
        <div
          role="alert"
          className="bg-red-50 border border-red-200 rounded-xl p-8 text-center max-w-md mx-auto"
        >
          <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" aria-hidden="true" />
          <p className="text-sm font-semibold text-red-800">Could not load the orders.</p>
          <p className="text-xs text-red-700 mt-1 mb-4">{loadError}</p>
          <button
            onClick={fetchOrders}
            className="px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      ) : (
        <OrderTable
          orders={orders}
          sortConfig={sortConfig}
          canManage={canManage}
          onSort={handleSort}
          onRowClick={handleRowClick}
          onQuickAction={handleQuickAction}
          loading={loading}
        />
      )}

      {!loading && !loadError && orders.length > 0 && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalItems={pagination.totalItems}
          itemsPerPage={pagination.itemsPerPage}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};

export default OrderList;
